/**
 * AuthInput — single source of truth for every input on the auth pages.
 *
 * Solves three things the old per-page inputs got wrong (or were
 * inconsistent on):
 *
 *  1. **Typed text visibility** — text is forced to #F2F0EB (cream),
 *     placeholders dim down to #52525A (still readable but secondary),
 *     and Chrome/Safari autofill is overridden via .auth-input rules
 *     in AuthShell so saved-credential text doesn't render as
 *     black-on-white on our dark card.
 *
 *  2. **Show/hide password toggle** — `withVisibilityToggle` opt-in. The
 *     toggle uses `onMouseDown={preventDefault}` so clicking the eye
 *     never steals focus from the input (critical for keyboard users
 *     who would otherwise lose their place on every toggle).
 *
 *  3. **Optional left icon** — used by the Register page's "License
 *     Key" field so the same component covers all input variants
 *     instead of forking the layout per page.
 *
 * Functional behavior is unchanged from the old per-page inputs — this
 * is a presentational consolidation, not a behavior change.
 */
import { forwardRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const AuthInput = forwardRef(function AuthInput(
  {
    id,
    label,
    type = 'text',
    value,
    onChange,
    placeholder,
    required,
    autoComplete,
    minLength,
    helperText,
    rightSlot,                  // optional ReactNode rendered inside the field on the right
    leftIcon,                   // optional ReactNode rendered inside the field on the left
    withVisibilityToggle,       // password fields: opts in to the eye icon toggle
    inputClassName = '',
    testId,
    onUpperCase,                // when true, value is upper-cased before onChange (license keys)
  },
  ref,
) {
  const [revealed, setRevealed] = useState(false);
  // Resolve the actual input type — only password fields with the toggle
  // can switch between hidden and visible.
  const effectiveType = withVisibilityToggle && revealed ? 'text' : type;

  const handleChange = (e) => {
    if (onUpperCase) {
      onChange({ ...e, target: { ...e.target, value: e.target.value.toUpperCase() } });
    } else {
      onChange(e);
    }
  };

  return (
    <div className="space-y-2">
      {label && (
        <Label
          htmlFor={id}
          className="text-[11px] font-semibold tracking-[0.15em] uppercase"
          style={{ color: 'var(--strata-text-muted, #8A8F99)', fontFamily: 'var(--strata-font-body, Montserrat), sans-serif' }}
        >
          {label}
        </Label>
      )}
      <div
        className="auth-input rounded-md transition-all duration-200 flex items-center"
        style={{ border: '1px solid var(--strata-border, #D5D8DD)', background: '#FFFFFF' }}
      >
        {leftIcon && (
          <div className="pl-3 flex-shrink-0" style={{ color: 'var(--strata-text-muted, #8A8F99)' }}>
            {leftIcon}
          </div>
        )}
        <Input
          ref={ref}
          id={id}
          type={effectiveType}
          value={value}
          onChange={handleChange}
          required={required}
          autoComplete={autoComplete}
          minLength={minLength}
          placeholder={placeholder}
          data-testid={testId}
          className={`h-11 bg-transparent border-0 placeholder:text-[#A1A6B0] focus-visible:ring-0 focus-visible:ring-offset-0 flex-1 ${inputClassName}`}
          // Inline color belt-and-suspenders so typed text is *always*
          // readable on the white card regardless of browser extensions,
          // autofill overrides, or dark-mode UA stylesheets.
          style={{
            background: 'transparent',
            color: 'var(--strata-text-primary, #111417)',
            caretColor: 'var(--strata-text-primary, #111417)',
            colorScheme: 'light',
            WebkitTextFillColor: 'var(--strata-text-primary, #111417)',
            fontFamily: 'var(--strata-font-body, Montserrat), sans-serif',
          }}
        />
        {/* Right-side controls — visibility toggle takes priority over a
            caller-provided rightSlot so password fields always work. */}
        {withVisibilityToggle ? (
          <button
            type="button"
            onClick={() => setRevealed(v => !v)}
            onMouseDown={(e) => e.preventDefault()}
            className="px-3 transition-colors"
            style={{ color: 'var(--strata-text-muted, #8A8F99)' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--strata-steel, #4575A8)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--strata-text-muted, #8A8F99)'}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            data-testid={testId ? `${testId}-toggle-visibility` : undefined}
            tabIndex={-1}
          >
            {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        ) : rightSlot}
      </div>
      {helperText && (
        <p className="text-[10px]" style={{ color: 'var(--strata-text-muted, #8A8F99)' }}>{helperText}</p>
      )}
    </div>
  );
});

export default AuthInput;
